/**
 * Newsletter Modal Component
 * Handles popup triggers, localStorage persistence, and form submission
 */

class NewsletterModal extends HTMLElement {
  constructor() {
    super();

    this.STORAGE_KEY = 'hab_newsletter_modal';
    this.delaySeconds = parseInt(this.dataset.delay, 10) || 5;
    this.dismissMinutes = parseInt(this.dataset.dismissMinutes, 10) || 1;
    this.enableTimer = this.dataset.enableTimer === 'true';
    this.enableExitIntent = this.dataset.enableExitIntent === 'true';

    this.hasShown = false;
    this.exitIntentHandler = null;
  }

  connectedCallback() {
    if (!this.shouldShowModal()) {
      return;
    }

    // Wait for DOM to be ready and modal-dialog to be moved to body
    setTimeout(() => {
      this.modal = document.querySelector('#NewsletterModal');
      if (!this.modal) {
        return;
      }

      this.form = this.modal.querySelector('form');
      this.closeButton = this.modal.querySelector('[id^="ModalClose-"]');
      this.dismissButton = this.modal.querySelector('.hab-newsletter-modal__dismiss');
      this.formWrapper = this.modal.querySelector('.hab-newsletter-modal__form-wrapper');
      this.successEl = this.modal.querySelector('.hab-newsletter-modal__success');
      this.errorEl = this.modal.querySelector('.hab-newsletter-modal__error');

      this.setupEventListeners();
      this.setupTriggers();
    }, 100);
  }

  setupEventListeners() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.handleDismiss());
    }

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', () => this.handleDismiss());
    }

    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.handleDismiss();
        }
      });

      this.modal.addEventListener('keyup', (e) => {
        if (e.code.toUpperCase() === 'ESCAPE') {
          this.handleDismiss();
        }
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // Ensure body class is removed when modal closes (backup cleanup)
    document.body.addEventListener('modalClosed', () => {
      document.body.classList.remove('overflow-hidden');
    });
  }

  setupTriggers() {
    if (this.enableTimer) {
      setTimeout(() => this.showModal(), this.delaySeconds * 1000);
    }

    if (this.enableExitIntent) {
      this.exitIntentHandler = (e) => {
        if (e.clientY < 0 && !this.hasShown) {
          this.showModal();
        }
      };
      document.addEventListener('mouseleave', this.exitIntentHandler);
    }
  }

  shouldShowModal() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return true;
      }

      const data = JSON.parse(stored);

      // If user subscribed, never show again
      if (data.type === 'subscribed') {
        return false;
      }

      // If dismissed, show again after dismissMinutes
      const expiry = data.timestamp + this.dismissMinutes * 60 * 1000;
      return Date.now() > expiry;
    } catch (e) {
      return true;
    }
  }

  setStorage(type) {
    try {
      localStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          type: type,
        })
      );
    } catch (e) {
      // localStorage not available
    }
  }

  showModal() {
    if (this.hasShown) {
      return;
    }

    this.hasShown = true;
    this.removeExitIntentListener();

    if (this.modal) {
      this.modal.show();
    }
  }

  hideModal() {
    if (this.modal) {
      this.modal.hide();
    }
  }

  removeExitIntentListener() {
    if (this.exitIntentHandler) {
      document.removeEventListener('mouseleave', this.exitIntentHandler);
      this.exitIntentHandler = null;
    }
  }

  handleDismiss() {
    this.setStorage('dismissed');
    this.hideModal();
  }

  async handleSubmit(e) {
    e.preventDefault();

    const emailInput = this.form.querySelector('input[type="email"]');
    const submitButton = this.form.querySelector('button[type="submit"]');

    if (!emailInput || !emailInput.value) {
      return;
    }

    const email = emailInput.value;
    const originalButtonText = submitButton.textContent;

    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';

    if (this.errorEl) {
      this.errorEl.classList.remove('is-visible');
    }

    try {
      const formData = new FormData(this.form);

      const response = await fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok || response.status === 200 || response.status === 302) {
        this.setStorage('subscribed');
        this.showSuccess();
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;

      if (this.errorEl) {
        this.errorEl.textContent = 'Something went wrong. Please try again.';
        this.errorEl.classList.add('is-visible');
      }
    }
  }

  showSuccess() {
    if (this.formWrapper) {
      this.formWrapper.classList.add('is-hidden');
    }

    if (this.successEl) {
      this.successEl.classList.add('is-visible');
    }

    setTimeout(() => this.hideModal(), 3000);
  }
}

customElements.define('newsletter-modal', NewsletterModal);
